import { addDays, addMonths } from '../constants/campaignConstants';

/**
 * Calculate campaign start and end dates using the same logic as useCampaignDateRange
 * This is a synchronous version for use in campaign parsing (list view)
 * 
 * @param {Object} campaign - Campaign object with required fields
 * @param {Array} campaignKiosks - Optional array of campaign kiosks (for statusId >= 10)
 * @param {number} defaultCampaignDurationDays - Default days to add to signed date (default: 45)
 * @param {number} paidEvergreenBills - Number of paid evergreen bills (default: 0)
 * @returns {Object} { startDate, endDate } - Calculated dates or null
 */
export const calculateCampaignDates = ({
  campaign,
  campaignKiosks = [],
  defaultCampaignDurationDays = 45,
  paidEvergreenBills = 0
}) => {
  if (!campaign || !campaign.id) {
    return { startDate: null, endDate: null };
  }

  const now = new Date();
  const signed = campaign.signedDate ? new Date(campaign.signedDate) : null;

  const estimatedStart = campaign.estimatedStartDate
    ? new Date(campaign.estimatedStartDate)
    : null;

  const estimatedEnd = campaign.estimatedEndDate
    ? new Date(campaign.estimatedEndDate)
    : null;

  const durationMonths = Number(campaign.campaignDuration || 0);
  // bonusMonths can be null in DB (default value), treat null as 0
  const bonusMonths = Number(campaign.bonusMonths ?? 0);

  const kiosk = campaignKiosks?.[0];

  let start = null;
  let end = null;

  if (campaign.statusId < 10) {
    if (estimatedStart && estimatedEnd) {
      start = estimatedStart;
      // Always add bonusMonths to estimatedEnd
      end = addMonths(estimatedEnd, bonusMonths);
    } else if (signed) {
      start = addDays(signed, defaultCampaignDurationDays);
      // Always add both durationMonths and bonusMonths
      end = addMonths(start, durationMonths + bonusMonths);
    } else {
      start = now;
      // Always add both durationMonths and bonusMonths
      end = addMonths(now, durationMonths + bonusMonths);
    }
  } else {
    const baseStart = kiosk?.reservationStart
      ? new Date(kiosk.reservationStart)
      : estimatedStart
      ? estimatedStart
      : addDays(signed || now, defaultCampaignDurationDays);

    start = baseStart;

    // Calculate base end date from reservation or start date + duration
    let baseEnd = kiosk?.reservationEnd
      ? new Date(kiosk.reservationEnd)
      : addMonths(start, durationMonths);

    const isEvergreenActive =
      campaign.evergreen &&
      !campaign.evergreenCancelDate &&
      now > baseEnd &&
      !kiosk?.reservationEnd;

    // Always add bonusMonths to baseEnd before checking evergreen
    baseEnd = addMonths(baseEnd, bonusMonths);

    end = isEvergreenActive
      ? addMonths(baseEnd, paidEvergreenBills)
      : baseEnd;
  }

  return {
    startDate: start,
    endDate: end
  };
};
